import * as mapboxgl from 'mapbox-gl';
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';

import { coordinateSearchResults } from './coordinate-query';

export const SEARCH_RESULT_ZOOM = 14;
export const COORDINATE_SEARCH_ZOOM = 13;

export interface MapSearchTexts {
  placeholder: string;
  noResults: string;
  searchFailed: string;
  searchUnavailable: string;
}

export const MAP_SEARCH_TEXT_KEYS: Record<keyof MapSearchTexts, string> = {
  placeholder: 'Search_for_a_place_or_coordinates_tr',
  noResults: 'No_results_found_tr',
  searchFailed: 'Search_failed_Please_try_again_tr',
  searchUnavailable: 'Map_search_is_currently_unavailable_tr',
};

export const MAP_SEARCH_FALLBACK_TEXTS: MapSearchTexts = {
  placeholder: 'Search for a place or coordinates',
  noResults: 'No results found',
  searchFailed: 'Search failed. Please try again.',
  searchUnavailable: 'Map search is currently unavailable.',
};

export type MapSearchRenderedTexts = Pick<MapSearchTexts, 'placeholder' | 'noResults' | 'searchFailed'>;

export interface MapSearchControlsOptions {
  accessToken: string;
  center?: { lat?: number | null; lng?: number | null };
  language?: string;
  texts?: Partial<MapSearchRenderedTexts>;
  onError: (messageKey: string) => void;
}

export interface MapSearchControlsHandle {
  geocoder: MapboxGeocoder | null;
  remove(): void;
}

function toProximity(center?: MapSearchControlsOptions['center']): MapboxGeocoder.LngLatLiteral | undefined {
  const lat = center?.lat;
  const lng = center?.lng;
  if (typeof lat !== 'number' || typeof lng !== 'number') return undefined;
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return undefined;
  return { latitude: lat, longitude: lng };
}

const escapeHtml = (text: string) =>
  text.replace(
    /[&<>"']/g,
    (c) => (({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }) as Record<string, string>)[c]
  );

function translateGeocoderMessages(geocoder: MapboxGeocoder, texts: MapSearchRenderedTexts): void {
  const internals = geocoder as unknown as {
    _renderMessage?: (msg: string) => void;
    _renderNoResults?: () => void;
    _renderError?: () => void;
  };
  if (typeof internals._renderMessage !== 'function') return;

  const render = internals._renderMessage.bind(geocoder);
  internals._renderNoResults = () =>
    render(
      `<div class='mapbox-gl-geocoder--error mapbox-gl-geocoder--no-results'>${escapeHtml(texts.noResults)}</div>`
    );
  internals._renderError = () =>
    render(`<div class='mapbox-gl-geocoder--error'>${escapeHtml(texts.searchFailed)}</div>`);
}

interface GeocoderResult {
  result?: {
    center?: [number, number];
    place_type?: string[];
    geometry?: { type?: string; coordinates?: [number, number] };
  };
}

function resultCenter(event: GeocoderResult): [number, number] | undefined {
  const result = event.result;
  const center = result?.center ?? (result?.geometry?.type === 'Point' ? result.geometry.coordinates : undefined);
  if (!center || center.length < 2 || !center.every(Number.isFinite)) return undefined;
  return center;
}

export function addMapSearchControls(map: mapboxgl.Map, opts: MapSearchControlsOptions): MapSearchControlsHandle {
  const texts: MapSearchRenderedTexts = { ...MAP_SEARCH_FALLBACK_TEXTS, ...(opts.texts ?? {}) };
  const proximity = toProximity(opts.center);
  const cleanups: Array<() => void> = [];

  let geocoder: MapboxGeocoder | null = null;
  try {
    geocoder = new MapboxGeocoder({
      accessToken: opts.accessToken,
      // The dialog already owns a draggable site marker; a second one would be read as a result.
      marker: false,
      // The geocoder normally fits a result's full bbox, which can be much larger than a city.
      // Fly to its center instead so cities and coordinates get the same predictable framing.
      flyTo: false,
      zoom: SEARCH_RESULT_ZOOM,
      placeholder: texts.placeholder,
      localGeocoder: (query: string) => coordinateSearchResults(query) as unknown as MapboxGeocoder.Result[],
      localGeocoderOnly: false,
      // An explicit proximity is only honoured with tracking off: the plugin's _updateProximity()
      // runs on add and nulls it below zoom 9 — where this dialog opens.
      ...(proximity ? { proximity, trackProximity: false } : { trackProximity: true }),
      ...(opts.language ? { language: opts.language } : {}),
    });
    translateGeocoderMessages(geocoder, texts);

    const onGeocoderError = () => opts.onError(MAP_SEARCH_TEXT_KEYS.searchFailed);
    const onGeocoderResult = (event: GeocoderResult) => {
      const center = resultCenter(event);
      if (center) {
        const zoom = event.result?.place_type?.includes('coordinate') ? COORDINATE_SEARCH_ZOOM : SEARCH_RESULT_ZOOM;
        map.flyTo({ center, zoom });
      }
    };
    geocoder.on('error', onGeocoderError);
    geocoder.on('result', onGeocoderResult);

    map.addControl(geocoder, 'top-right');
    // Stable selector for screenshot and accessibility-oriented E2E steps.
    const geocoderInput = document.querySelector('.mapboxgl-ctrl-geocoder input');
    geocoderInput?.setAttribute('data-testid', 'map-search-field');

    const attached = geocoder;
    cleanups.push(() => attached.off('error', onGeocoderError));
    cleanups.push(() => attached.off('result', onGeocoderResult));
    cleanups.push(() => map.removeControl(attached));
  } catch (err) {
    console.error('Map search field could not be added', err);
    geocoder = null;
    opts.onError(MAP_SEARCH_TEXT_KEYS.searchUnavailable);
  }

  return {
    geocoder,
    remove() {
      for (const cleanup of cleanups) {
        try {
          cleanup();
        } catch {
          /* already detached, or removed together with the map */
        }
      }
    },
  };
}
